import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../services/user.service'
import { FeatureFlagsService } from '../services/feature-flags.service'
import { ZebraService } from '../services/zebra.service'
import { ToastrService } from 'ngx-toastr';
import { UserPermissionsService } from '../services/user-permissions.service';
import { HasPermissionDirective } from '../directives/has-permission.directive';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ThemeSwitcherComponent } from '../components/theme-switcher/theme-switcher.component';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, HasPermissionDirective, TranslocoModule, ThemeSwitcherComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit, OnDestroy {
  printerName: string = '';
  printFormat: string = 'ZPL';
  printFormatOptions = [
    { value: 'ZPL', label: 'ZPL' },
    { value: 'PDF', label: 'PDF' }
  ];
  // Nova UI de impressoras
  showNewPrinterUI: boolean = false;
  printers: Array<{ Name: string; IsOnline: boolean; IsDefault: boolean; Status: string }> = [];
  isLoadingPrinters: boolean = false;
  printersLoadError: string | null = null;
  private printersIntervalId: number | null = null;
  private isFetchingPrinters: boolean = false;
  isReadOnly: boolean = false; // Modo somente leitura quando não tem permissão de escrita

  constructor(private userService: UserService,
    private featureFlags: FeatureFlagsService,
    private zebraService: ZebraService,
    private toastr: ToastrService,
    private permissionsService: UserPermissionsService,
    private transloco: TranslocoService
  ) {

  }
  ngOnInit(): void {
    // Verificar permissão de escrita
    this.permissionsService.loadUserPermissions().then(() => {
      this.isReadOnly = !this.permissionsService.hasPermission('settings.edit');
    });

    this.userService.getMe()
      .then(result => {
        if (result) {
          this.printerName = result.printerName ?? '';
          this.printFormat = result.printFormat ?? 'ZPL';
        }
      })
      .finally(async () => {
        const customerId = localStorage.getItem('customerId') || undefined;
        try {
          this.showNewPrinterUI = await this.featureFlags.hasFeature('frontend-enable-new-printer-screen', customerId);
        } catch (e) {
          this.showNewPrinterUI = false;
        }
        if (this.showNewPrinterUI) {
          await this.loadPrinters(true, false);
          this.printersIntervalId = window.setInterval(() => {
            this.loadPrinters(false, true);
          }, 3000);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.printersIntervalId !== null) {
      clearInterval(this.printersIntervalId);
      this.printersIntervalId = null;
    }
  }

  onSubmit() {
    if (!this.permissionsService.hasPermission('settings.edit')) {
      this.toastr.error(this.transloco.translate('settings.toast.noEditPermission'), this.transloco.translate('toasts.accessDenied'));
      return;
    }

    if (this.printerName.trim()) {
      this.userService.saveSettings(this.printerName.trim(), this.printFormat)
        .then((result) => {
          if (result) {
            localStorage.setItem('printerName', this.printerName.trim());
            localStorage.setItem('printFormat', this.printFormat);
            this.toastr.success(this.transloco.translate('settings.toast.saved'), this.transloco.translate('toasts.success'));
          }
          else {
            this.toastr.error(this.transloco.translate('settings.toast.saveError'), this.transloco.translate('toasts.error'));
          }
        });
    } else {
      this.toastr.warning(this.transloco.translate('settings.toast.enterPrinterName'));
    }
  }

  

  private async loadPrinters(initial: boolean = false, silent: boolean = false) {
    if (this.isFetchingPrinters) return;
    this.isFetchingPrinters = true;
    if (initial && !silent) {
      this.isLoadingPrinters = true;
      this.printersLoadError = null;
    }
    try {
      const list = await this.zebraService.listPrinters();
      this.printers = Array.isArray(list) ? list : [];
      const currentExists = this.printerName && this.printers.some(p => p.Name === this.printerName);
      if (!currentExists) {
        const stored = (localStorage.getItem('printerName') || '').trim();
        if (stored && this.printers.some(p => p.Name === stored)) {
          this.printerName = stored;
        } else if (initial) {
          const def = this.printers.find(p => p.IsDefault);
          if (def) {
            this.printerName = def.Name;
          }
        }
      }
    } catch (e) {
      console.error(e);
      if (!silent) {
        this.printersLoadError = this.transloco.translate('settings.loadPrintersError');
      }
    } finally {
      if (initial && !silent) {
        this.isLoadingPrinters = false;
      }
      this.isFetchingPrinters = false;
    }
  }

  trackByName(index: number, p: { Name: string }): string {
    return p.Name;
  }

  onPrinterCardClick(name: string) {
    if (!this.isReadOnly) {
      this.printerName = name;
    }
  }

  async onPrintTest() {
    const selected = (this.printerName || '').trim();
    if (!selected) {
      this.toastr.warning(this.transloco.translate('settings.toast.selectPrinterToTest'));
      return;
    }
    const previous = localStorage.getItem('printerName');
    try {
      localStorage.setItem('printerName', selected);
      const now = new Date();
      const dt = now.toLocaleString('pt-BR');
      const customerId = localStorage.getItem('customerId') || undefined;
      let useEpl = false;
      try {
        useEpl = await this.featureFlags.hasFeature('frontend-enable-new-printer-screen-user-epl-test', customerId);
      } catch {
        useEpl = false;
      }

      const payload = useEpl
        ? `N\nA50,50,0,3,1,1,N,"Flowtify Printer Test"\nA50,100,0,2,1,1,N,"${dt}"\nP1\n`
        : `^XA\n^CF0,40\n^FO50,50^FDFlowtify Printer Test^FS\n^CF0,30\n^FO50,100^FD${dt}^FS\n^XZ`;

      const ok = await this.zebraService.sendToZebraPrinter(payload);
      if (ok) {
        this.toastr.success(this.transloco.translate('settings.toast.testSent'));
      } else {
        this.toastr.error(this.transloco.translate('settings.toast.testFailed'));
      }
    } catch (e) {
      console.error(e);
      this.toastr.error(this.transloco.translate('settings.toast.testError'));
    } finally {
      if (previous !== null) {
        localStorage.setItem('printerName', previous);
      } else {
        localStorage.removeItem('printerName');
      }
    }
  }
}
